import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CreditNoteForm = () => {
  const [formData, setFormData] = useState({
    sales_contract_id: '',
    credit_note_date: '',
    credit_note_number: '',
    ref_no: '',
    buyer_name: '',
    buyer_id: '',
    cha_cma_id: '',
    warehouse_id: '',
    warehouse_location: '',
    state_code: '',
    buyer_gst: '',
    kabl_state_code: '',
    particulars: '',
    amount: '',
    gst_amount: '',
    total_amount: '',
    check_cma_cha: 'CMA',
    issuing_authority_id: '' // Added field
  });

  const [loading, setLoading] = useState(false);
  const [salesContracts, setSalesContracts] = useState([]);
  const [cmaList, setCmaList] = useState([]);
  const [chaList, setChaList] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [chaWarehouses, setChaWarehouses] = useState([]);
  const [kribhcoList, setKribhcoList] = useState([]);
  const [issuingAuthorities, setIssuingAuthorities] = useState([]); // Added state
  const [selectedType, setSelectedType] = useState('CMA');

  const navigate = useNavigate();

  const FIELD_LIMITS = {
    sales_contract_id: 10,
    credit_note_date: 10,
    credit_note_number: 50,
    ref_no: 100,
    buyer_name: 200,
    cha_cma_id: 10,
    warehouse_id: 10,
    warehouse_location: 200,
    state_code: 50,
    buyer_gst: 15,
    kabl_state_code: 10,
    particulars: 1000,
    amount: 14,
    gst_amount: 14,
    total_amount: 14,
    issuing_authority_id: 10 // Added field limit
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.toString().length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace(/_/g, ' ')}`;
    }
    const requiredFields = [
      'sales_contract_id', 'credit_note_date', 'credit_note_number', 
      'cha_cma_id', 'warehouse_id', 'particulars', 'amount', 'issuing_authority_id' // Added
    ];
    if (requiredFields.includes(name) && (!value || value === '' || value === 0)) {
      error = `${name.replace(/_/g, ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  // Fetch dropdown data including issuing authorities
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [salesRes, cmaRes, chaRes, kribhcoRes, issuingAuthorityRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/masters/dropdown-sales-contracts`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-cha-list`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-kribhco`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-issuing-authorities`, { credentials: 'include' }) // Added
        ]);

        const [salesData, cmaData, chaData, kribhcoData, issuingAuthorityData] = await Promise.all([
          salesRes.json(),
          cmaRes.json(),
          chaRes.json(),
          kribhcoRes.json(),
          issuingAuthorityRes.json() // Added
        ]);

        if (salesRes.ok && Array.isArray(salesData.data)) setSalesContracts(salesData.data);
        if (cmaRes.ok && Array.isArray(cmaData.data)) setCmaList(cmaData.data);
        if (chaRes.ok && Array.isArray(chaData.data)) setChaList(chaData.data);
        if (kribhcoRes.ok && Array.isArray(kribhcoData.data)) setKribhcoList(kribhcoData.data);
        if (issuingAuthorityRes.ok && Array.isArray(issuingAuthorityData.data)) {
          setIssuingAuthorities(issuingAuthorityData.data);
        }
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
        toast.error('Failed to load dropdown data');
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch warehouses by CMA
  const fetchWarehousesByCma = async (cmaId) => {
    if (!cmaId) {
      setWarehouseList([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${cmaId}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setWarehouseList(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching CMA warehouses:', error);
      setWarehouseList([]);
    }
  };

  // Fetch warehouses by CHA
  const fetchWarehousesByCha = async (chaId) => {
    if (!chaId) {
      setChaWarehouses([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cha?cha_cma_id=${chaId}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setChaWarehouses(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching CHA warehouses:', error);
      setChaWarehouses([]);
    }
  };

  // Fetch sales contract details when selected
  useEffect(() => {
    if (formData.sales_contract_id) {
      const fetchSalesContractDetails = async () => {
        try {
          const res = await fetch(
            `${API_BASE_URL}/api/masters/sales-contract/${formData.sales_contract_id}`,
            { credentials: 'include' }
          );
          const data = await res.json();
          if (res.ok && data.data) {
            setFormData(prev => ({
              ...prev,
              buyer_name: data.data.buyer_name || '',
              buyer_id: data.data.buyer_id || '',
              buyer_gst: data.data.buyer_gst || ''
            }));
          }
        } catch (err) {
          console.error('Error fetching sales contract details:', err);
        }
      };
      fetchSalesContractDetails();
    }
  }, [formData.sales_contract_id]);

  // Auto-populate warehouse location and state code
  useEffect(() => {
    if (formData.warehouse_id) {
      let selectedWarehouse = null;
      
      if (selectedType === 'CMA' && warehouseList.length > 0) {
        selectedWarehouse = warehouseList.find(
          wh => wh.warehouse_id.toString() === formData.warehouse_id.toString()
        );
      } else if (selectedType === 'CHA' && chaWarehouses.length > 0) {
        selectedWarehouse = chaWarehouses.find(
          wh => wh.warehouse_id.toString() === formData.warehouse_id.toString()
        );
      }
      
      if (selectedWarehouse) {
        setFormData(prev => ({
          ...prev,
          warehouse_location: selectedWarehouse.location || '',
          state_code: selectedWarehouse.state_code || ''
        }));
      }
    }
  }, [formData.warehouse_id, warehouseList, chaWarehouses, selectedType]);

  // Calculate total amount when amount or GST changes
  useEffect(() => {
    const amount = parseFloat(formData.amount) || 0;
    const gstAmount = parseFloat(formData.gst_amount) || 0;
    const total = amount + gstAmount;
    setFormData(prev => ({
      ...prev,
      total_amount: total.toFixed(2)
    }));
  }, [formData.amount, formData.gst_amount]);

  // Handle type change (CMA/CHA)
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setFormData(prev => ({ 
      ...prev, 
      cha_cma_id: '', 
      check_cma_cha: type,
      warehouse_id: '',
      warehouse_location: '',
      state_code: ''
    }));
    setWarehouseList([]);
    setChaWarehouses([]);
  };

  const getMaxDate = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const preventDateTyping = (e) => {
    if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'credit_note_date' && value) {
      const maxDate = getMaxDate();
      if (value > maxDate) {
        toast.error('Future dates are not allowed for Credit Note Date.');
        return;
      }
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    if (fieldName === 'cha_cma_id') {
      if (selectedType === 'CMA') {
        fetchWarehousesByCma(value);
      } else {
        fetchWarehousesByCha(value);
      }
      setFormData(prev => ({ 
        ...prev, 
        warehouse_id: '', 
        warehouse_location: '', 
        state_code: '' 
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const maxDate = getMaxDate();
    if (formData.credit_note_date && formData.credit_note_date > maxDate) {
      toast.error('Future dates are not allowed for Credit Note Date.');
      return;
    }

    setLoading(true);

    // Field limit validation
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].toString().length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace(/_/g, ' ')}`);
        setLoading(false);
        return;
      }
    }

    // Required fields validation
    const requiredFields = [
      'sales_contract_id', 'credit_note_date', 'credit_note_number',
      'cha_cma_id', 'warehouse_id', 'particulars', 'amount', 'issuing_authority_id' // Added
    ];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field] === '' || formData[field] === 0) {
        toast.error(`${field.replace(/_/g, ' ')} is required`);
        setLoading(false);
        return;
      }
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/sales/credit-note-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Credit Note Created Successfully!');
        // Reset form
        setFormData({
          sales_contract_id: '',
          credit_note_date: '',
          credit_note_number: '',
          ref_no: '',
          buyer_name: '',
          buyer_id: '',
          cha_cma_id: '',
          warehouse_id: '',
          warehouse_location: '',
          state_code: '',
          buyer_gst: '',
          kabl_state_code: '',
          particulars: '',
          amount: '',
          gst_amount: '',
          total_amount: '',
          check_cma_cha: 'CMA',
          issuing_authority_id: ''
        });
        setWarehouseList([]);
        setChaWarehouses([]);
        setSelectedType('CMA');
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Server Error');
    }

    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Credit Note Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#2691cc] border border-[#2691cc] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Sales Contract Number */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Sales Contract Number <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={salesContracts}
                value={formData.sales_contract_id}
                onChange={(value) => handleDropdownChange('sales_contract_id', value)}
                placeholder="Select Sales Contract"
                displayKey="sales_contract_no"
                valueKey="sales_contract_id"
                required
                onBlur={handleBlur}
              />
            </div>

            {/* Credit Note Date */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Credit Note Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="credit_note_date"
                value={formData.credit_note_date}
                onChange={handleChange}
                onKeyDown={preventDateTyping}
                max={getMaxDate()}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none cursor-pointer"
                title="Select date from calendar only (future dates not allowed)"
                required
              />
            </div>

            {/* Credit Note Number */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Credit Note Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="credit_note_number"
                value={formData.credit_note_number}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.credit_note_number}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                placeholder="Enter credit note number"
                required
              />
            </div>

            {/* Ref No */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Ref No
              </label>
              <input
                type="text"
                name="ref_no"
                value={formData.ref_no}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.ref_no}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                placeholder="Enter reference number"
              />
            </div>

            {/* Buyer Name (Auto-populated) */}
            <div>
              <label className="block text-xs font-medium mb-1">Buyer Name</label>
              <input
                type="text"
                name="buyer_name"
                value={formData.buyer_name}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none cursor-not-allowed"
                placeholder="Auto-populated from Sales Contract"
                readOnly
              />
            </div>

            {/* Buyer GST (Auto-populated) */}
            <div>
              <label className="block text-xs font-medium mb-1">Buyer GST</label>
              <input
                type="text"
                name="buyer_gst"
                value={formData.buyer_gst}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none cursor-not-allowed"
                placeholder="Auto-populated from Buyer"
                readOnly
              />
            </div>

            {/* CMA/CHA Checkbox Section */}
            <div className="md:col-span-2 flex gap-6 items-center mb-2">
              <label className="flex items-center gap-1">
                <input 
                  type="checkbox" 
                  checked={selectedType === 'CMA'} 
                  onChange={() => handleTypeChange('CMA')} 
                /> 
                CMA
              </label>
              <label className="flex items-center gap-1">
                <input 
                  type="checkbox" 
                  checked={selectedType === 'CHA'} 
                  onChange={() => handleTypeChange('CHA')} 
                /> 
                CHA
              </label>
            </div>

            {/* Conditional CMA Dropdown */}
            {selectedType === 'CMA' && (
              <div>
                <label className="block text-xs font-medium mb-1">
                  CMA <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  options={cmaList
                    .filter(cma => cma.type === 'CMA' || cma.type === 'BOTH')
                    .map(cma => ({
                      id: cma.cha_cma_id,
                      name: cma.name
                    }))}
                  value={formData.cha_cma_id}
                  onChange={(value) => handleDropdownChange('cha_cma_id', value)}
                  placeholder="Select CMA"
                  displayKey="name"
                  valueKey="id"
                  required
                  onBlur={handleBlur}
                />
              </div>
            )}

            {/* Conditional CHA Dropdown */}
            {selectedType === 'CHA' && (
              <div>
                <label className="block text-xs font-medium mb-1">
                  CHA <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  options={chaList
                    .filter(cha => cha.type === 'CHA' || cha.type === 'BOTH')
                    .map(cha => ({
                      id: cha.cha_cma_id,
                      name: cha.name
                    }))}
                  value={formData.cha_cma_id}
                  onChange={(value) => handleDropdownChange('cha_cma_id', value)}
                  placeholder="Select CHA"
                  displayKey="name"
                  valueKey="id"
                  required
                  onBlur={handleBlur}
                />
              </div>
            )}

            {/* Conditional Warehouse Dropdown for CMA */}
            {selectedType === 'CMA' && (
              <div>
                <label className="block text-xs font-medium mb-1">
                  Warehouse Name <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  options={warehouseList.map(warehouse => ({
                    id: warehouse.warehouse_id,
                    name: `${warehouse.name} (${warehouse.location || 'N/A'})`
                  }))}
                  value={formData.warehouse_id}
                  onChange={(value) => handleDropdownChange('warehouse_id', value)}
                  placeholder="Select Warehouse"
                  displayKey="name"
                  valueKey="id"
                  required
                  disabled={!formData.cha_cma_id}
                  onBlur={handleBlur}
                />
              </div>
            )}

            {/* Conditional Warehouse Dropdown for CHA */}
            {selectedType === 'CHA' && (
              <div>
                <label className="block text-xs font-medium mb-1">
                  Warehouse/Port <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  options={chaWarehouses.map(warehouse => ({
                    id: warehouse.warehouse_id,
                    name: `${warehouse.name} (${warehouse.location || 'N/A'})`
                  }))}
                  value={formData.warehouse_id}
                  onChange={(value) => handleDropdownChange('warehouse_id', value)}
                  placeholder="Select Warehouse/Port"
                  displayKey="name"
                  valueKey="id"
                  required
                  disabled={!formData.cha_cma_id}
                  onBlur={handleBlur}
                />
              </div>
            )}

            {/* Warehouse Location (Auto-populated) */}
            <div>
              <label className="block text-xs font-medium mb-1">Warehouse Location</label>
              <input
                type="text"
                name="warehouse_location"
                value={formData.warehouse_location}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none cursor-not-allowed"
                placeholder="Auto-populated"
                readOnly
              />
            </div>

            {/* State Code (Auto-populated) */}
            <div>
              <label className="block text-xs font-medium mb-1">State Code</label>
              <input
                type="text"
                name="state_code"
                value={(() => {
                  if (!formData.warehouse_id) return '';
                  let selectedWarehouse = null;
                  if (selectedType === 'CMA' && warehouseList.length > 0) {
                    selectedWarehouse = warehouseList.find(
                      wh => wh.warehouse_id.toString() === formData.warehouse_id.toString()
                    );
                  } else if (selectedType === 'CHA' && chaWarehouses.length > 0) {
                    selectedWarehouse = chaWarehouses.find(
                      wh => wh.warehouse_id.toString() === formData.warehouse_id.toString()
                    );
                  }
                  if (selectedWarehouse && selectedWarehouse.state && selectedWarehouse.state_code) {
                    return `${selectedWarehouse.state} (${selectedWarehouse.state_code})`;
                  }
                  return formData.state_code;
                })()}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none cursor-not-allowed"
                placeholder="Auto-populated"
                readOnly
              />
            </div>

            {/* KABL State Code */}
            <div>
              <label className="block text-xs font-medium mb-1">KABL State Code</label>
              <SearchableDropdown
                options={kribhcoList.map(kribhco => {
                  const stateInfo = kribhco.state && kribhco.state_code 
                    ? `${kribhco.state} (${kribhco.state_code})`
                    : kribhco.state || kribhco.state_code || '';
                  return {
                    id: kribhco.id,
                    name: stateInfo
                  };
                })}
                value={formData.kabl_state_code}
                onChange={(value) => handleDropdownChange('kabl_state_code', value)}
                placeholder="Select KABL State"
                displayKey="name"
                valueKey="id"
                onBlur={handleBlur}
              />
            </div>

            {/* Issuing Authority - NEW FIELD */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Issuing Authority <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={issuingAuthorities}
                value={formData.issuing_authority_id}
                onChange={(value) => handleDropdownChange('issuing_authority_id', value)}
                placeholder="Select Issuing Authority"
                displayKey="authority_name"
                valueKey="id"
                required
                onBlur={handleBlur}
              />
            </div>

            {/* Amount */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                placeholder="Enter amount"
                required
              />
            </div>

            {/* GST Amount */}
            <div>
              <label className="block text-xs font-medium mb-1">GST (if any)</label>
              <input
                type="number"
                step="0.01"
                name="gst_amount"
                value={formData.gst_amount}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                placeholder="Enter GST amount"
              />
            </div>

            {/* Total Amount (Calculated) */}
            <div>
              <label className="block text-xs font-medium mb-1">Total Amount</label>
              <input
                type="text"
                name="total_amount"
                value={formData.total_amount}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 font-semibold focus:outline-none rounded-none cursor-not-allowed"
                placeholder="Auto-calculated"
                readOnly
              />
            </div>

            {/* Particulars */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1">
                Particulars <span className="text-red-500">*</span>
              </label>
              <textarea
                name="particulars"
                value={formData.particulars}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.particulars}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none resize-none"
                rows={3}
                placeholder="Enter particulars/description for the credit note"
                required
              />
            </div>
          </div>
        </div>

        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#2691cc] text-white font-semibold tracking-wide rounded border border-[#2691cc] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            style={{ minWidth: '100px' }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Credit Note'}
          </button>
        </div>
      </form>

      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false} 
        newestOnTop 
        closeOnClick 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
      />
    </div>
  );
};

export default CreditNoteForm;
